import { useStore } from '@nanostores/react';
import { formatDate, differenceInDays } from 'date-fns';
import { Button } from '~/components/ui/Button';
import * as Tooltip from '~/components/ui/Tooltip';
import { userStore } from '~/lib/stores/user';
import { formatTokens } from '~/utils/format';
import { getAbbreviatedTimezone } from '~/utils/getAbbreviatedTimezone';
import { personalPricingList } from '~/utils/pricing';

interface TokenUsageHeaderProps {
  tokens?: number;
  billingDate?: number | Date;
  canManageBilling?: boolean;
  onManageBilling: () => void;
  isLoading?: boolean;
  isTeam?: boolean;
}

export const TokenUsageHeader = ({
  tokens,
  billingDate,
  canManageBilling = true,
  onManageBilling,
  isLoading,
  isTeam = false,
}: TokenUsageHeaderProps) => {
  const user = useStore(userStore.user);
  const personalSubscriptionBeingDowngraded = useStore(userStore.isSubscriptionBeingDowngraded);
  const personalSubscriptionBeingCanceled = useStore(userStore.isSubscriptionBeingCanceled);
  const currentPeriodEnd = user?.membership?.subscription?.currentPeriodEnd;
  const isYearlySubscription = user?.membership?.subscription?.interval === 'yearly';

  const isYearlySubscriptionEndInTheCurrentMonth =
    isYearlySubscription && currentPeriodEnd ? differenceInDays(new Date(currentPeriodEnd), new Date()) <= 30 : false;

  let displayTokens = formatTokens(tokens);
  let endOfMessage = '';

  if (personalSubscriptionBeingDowngraded && !isTeam && user?.membership?.nextTier) {
    const pricingTier = personalPricingList.find((pricing) => pricing.tier === user?.membership?.nextTier);
    displayTokens = pricingTier?.tokens || formatTokens(tokens);

    // if the subscription downgrade is happening in the current month we display the tokens of the next tier, otherwise we display the current tokens
    if (isYearlySubscription) {
      displayTokens = isYearlySubscriptionEndInTheCurrentMonth
        ? pricingTier?.tokens || formatTokens(tokens)
        : formatTokens(tokens);
    }
  }

  if (isTeam) {
    endOfMessage = ' and will be shared evenly';
  }

  if (personalSubscriptionBeingCanceled && !isTeam) {
    endOfMessage = ' is\u00A0canceled';

    // for users on the annual pro plan we display info about cancellation only on the last month of their subscription
    if (isYearlySubscription) {
      endOfMessage = isYearlySubscriptionEndInTheCurrentMonth ? ' is\u00A0canceled' : '';
    }
  }

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-8">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">Subscription & Tokens</h2>

        {!!tokens && !!billingDate && (
          <span className="text-sm text-bolt-ds-textSecondary max-w-112">
            Your next{isTeam ? ' team' : ''} token refill of{' '}
            <span className="text-bolt-ds-textPrimary font-semibold">{displayTokens} tokens</span> is due on{' '}
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <span className="text-bolt-ds-textPrimary font-semibold text-nowrap inline-flex border-b border-b-dashed border-b-bolt-ds-textSecondary">
                    {formatDate(billingDate, 'MMMM d, yyyy')}
                  </span>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content side="top" align="center" sideOffset={4}>
                    Refill time: {formatDate(billingDate, 'yyyy-MM-dd h:mm a')} ({getAbbreviatedTimezone()})
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
            {endOfMessage}.
          </span>
        )}
      </div>

      {canManageBilling && (
        <Button
          variant="secondary"
          size="md"
          onClick={onManageBilling}
          startIcon="i-heroicons:arrow-top-right-on-square"
          loading={isLoading}
        >
          Manage billing
        </Button>
      )}
    </div>
  );
};
